/**
 * GetCampaignRankingContentMostCoinsUseCase
 * Loads the content (most-coins) leaderboard for a campaign and remaps to
 * entities, branching live-vs-ended on `campaign.active`.
 *
 * Flutter source of truth:
 * - lib/data/repository/campaigns_repository_impl.dart:33-41 —
 *   getCampaignRankingContentMostCoins(campaign): if `campaign.active` →
 *   getCampaignContentMostCoins(campaign.id, campaign.rankingFetchLimit); else →
 *   getEndedCampaignContentMostCoins(campaign.id, campaign.rankingFetchLimit).
 * - lib/presentation/bottom_sheet/campaign_ranking_bottom_sheet.dart:128-136 —
 *   CampaignType.contentMostCoins branch calls this repository method.
 *
 * Source-shape selection (Task 3 remappers): the live `media` collection-group
 * query yields the "from media" doc shape
 * (CampaignRankingContentRemapper.fromMediaJson); the frozen `ranking`
 * subcollection yields the ranking doc shape (fromRankingJson).
 */

import { CampaignsService } from '@/domain/services/campaign/campaignsService';
import { CampaignRankingContentRemapper } from '@/domain/remappers/campaign/campaignRankingContentRemapper';
import type { CampaignEntity } from '@/domain/entities/campaign/campaignEntity';
import type { CampaignRankingContentEntity } from '@/domain/entities/campaign/campaignRankingContentEntity';

export class GetCampaignRankingContentMostCoinsUseCase {
  private campaignsService: CampaignsService;

  constructor() {
    this.campaignsService = CampaignsService.getInstance();
  }

  async execute(campaign: CampaignEntity): Promise<CampaignRankingContentEntity[]> {
    if (campaign.active) {
      const items = await this.campaignsService.getCampaignContentMostCoins(
        campaign.id,
        campaign.rankingFetchLimit,
      );
      return CampaignRankingContentRemapper.fromMediaJsonList(items).map(
        CampaignRankingContentRemapper.fromDto,
      );
    }

    const items = await this.campaignsService.getEndedCampaignContentMostCoins(
      campaign.id,
      campaign.rankingFetchLimit,
    );
    return CampaignRankingContentRemapper.fromRankingJsonList(items).map(
      CampaignRankingContentRemapper.fromDto,
    );
  }
}

export const createGetCampaignRankingContentMostCoinsUseCase = () =>
  new GetCampaignRankingContentMostCoinsUseCase();
